import { AxiosError } from 'axios'
import { ApiClient } from '.'
import { User } from '../../domain/User'
import { AppRequestOptions, get } from './httpsWrappers'
import {
  AuthClient,
  AuthSource,
  GuestAuthRequest,
  guestAuthRequestPath,
  guestRandomHandlePath,
  healthCheckPath,
  identityHealthCheckPath,
  identityUrl,
  SessionResponse,
} from './IdentityTypes'
import { Logger } from '../../shared/Logger'

export const requestRandomHandle = async (): Promise<string> => {
  const response = await get<{ handle: string }>(
    identityUrl,
    guestRandomHandlePath,
    { noAuthorization: true },
  )
  return response.handle
}

export const requestGuestAuthToken = async (
  deviceCode: string | null,
  handle: string,
): Promise<AuthClient> => {
  if (!deviceCode) {
    throw new Error('can not request auth without deviceCode')
  }
  const request: GuestAuthRequest = {
    deviceCode,
    handle,
  }
  const sessionResponse: SessionResponse = await ApiClient.post<SessionResponse>(
    identityUrl,
    guestAuthRequestPath,
    request,
    { noAuthorization: true },
  )
  return { ...sessionResponse, authSource: AuthSource.guest }
}

export const updateUserHandle = async (handle: string): Promise<void> => {
  Logger.warn(`update handle not implemented - handle was ${handle}`)
  return
}

export type IdentityHealthCheckResponse = { health: 'healthy'; info: User }
export type HealthCheckResponse = {
  health: string
  current_timestamp: Date
  environment: string
}

export const getIdentityHealthCheck = async (
  user: User | null,
  sessionToken: string | null | undefined,
  deviceCode: string | null | undefined,
): Promise<IdentityHealthCheckResponse | HealthCheckResponse> => {
  if (!user) {
    Logger.debug(`identity healthcheck failure - without comparison user`)
    throw new Error(`identity healthcheck failure - without comparison user`)
  }
  if (!sessionToken) {
    Logger.debug(`identity healthcheck failure - without sessionToken`)
    throw new Error(`identity healthcheck failure - without sessionToken`)
  }
  try {
    const result = await ApiClient.get<IdentityHealthCheckResponse>(
      identityUrl,
      identityHealthCheckPath,
      { userId: user.userId, sessionToken, deviceCode: deviceCode || undefined },
    )
    if (result?.info?.userId !== user.userId) {
      Logger.debug(
        `expected user ${user.handle} - ${user.userId} but got ${result?.info?.handle} - ${result?.info?.userId}`,
      )
      throw new Error(
        `identity healthcheck failure - did not find expected user`,
      )
    }
    return result
  } catch (ex) {
    const result: HealthCheckResponse = await getHealthCheck() // throw this if neither succeeds, ie API is down
    return result
    throw new Error(
      `identity healthcheck failure - ${(ex as AxiosError).message}`,
    )
  }
}

export const getHealthCheck = async (): Promise<HealthCheckResponse> => {
  try {
    const healthResponse = await ApiClient.get<HealthCheckResponse>(
      identityUrl,
      healthCheckPath,
      { noAuthorization: true },
    )
    if (!healthResponse?.health) {
      throw new Error('healthcheck failure')
    }
    return healthResponse
  } catch (ex) {
    throw new Error(`healthcheck failure`)
  }
}
